import { useCallback, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router";

import { AIBadge } from "@/shared/components/ai";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { Tab, Tabs, TabsList } from "@/shared/foundation/primitives";

import { DatasetActionStatus } from "./DatasetActionStatus";
import styles from "./DatasetTabs.module.css";
import { useActiveDatasetTab, DatasetTab } from "./useActiveDatasetTabFromUrl";

interface DatasetTabsProps {
  datasetId: string;
}

/**
 * Renders the dataset detail view's tab strip; the active tab is derived from
 * the URL by `useActiveDatasetTab`.
 *
 * Each tab link preserves the current `location.search` so query params
 * survive tab switches. The Files tab additionally preserves its last-visited
 * pathname (see `handleTabSwitch`) so deep directory navigation is restored
 * on return.
 */
export function DatasetTabs({ datasetId }: DatasetTabsProps) {
  const [activeTab, setActiveTab] = useActiveDatasetTab();
  const [filePath, setFilePath] = useState(DatasetTab.Files as string);
  const location = useLocation();
  const sessionsTabEnabled = useCheckFeatureFlag("release-sessions");

  const handleTabSwitch = useCallback(
    (currentTab: DatasetTab, nextTab: DatasetTab) => {
      // Files supports deep directory paths under its base route; capture
      // the current pathname on exit so the Files tab's `to` restores the
      // last-visited subdirectory on return.
      if (currentTab === DatasetTab.Files) {
        setFilePath(location.pathname);
      }

      setActiveTab(nextTab);
    },
    [location, setActiveTab, setFilePath],
  );

  const tabs: { tab: DatasetTab; label: ReactNode; pathname?: string }[] = [
    { tab: DatasetTab.Files, label: "Files", pathname: filePath },
    ...(sessionsTabEnabled
      ? [{ tab: DatasetTab.Sessions, label: "Sessions" }]
      : []),
    { tab: DatasetTab.Topics, label: "Topics" },
    { tab: DatasetTab.Events, label: "Events" },
    {
      tab: DatasetTab.Actions,
      label: (
        <>
          Actions
          <DatasetActionStatus datasetId={datasetId} />
        </>
      ),
    },
    { tab: DatasetTab.AgentThreads, label: "Agents" },
    { tab: DatasetTab.Details, label: "Details" },
    { tab: DatasetTab.Permissions, label: "Permissions" },
    {
      tab: DatasetTab.AISummary,
      label: (
        <>
          <AIBadge />
          AI
        </>
      ),
    },
  ];

  return (
    <Tabs
      className={styles.tabs}
      value={activeTab}
      onValueChange={(nextTab: DatasetTab) => {
        handleTabSwitch(activeTab, nextTab);
      }}
    >
      <TabsList aria-label="Dataset">
        {tabs.map(({ tab, label, pathname = tab }) => (
          <Tab
            key={tab}
            value={tab}
            nativeButton={false}
            render={<Link to={{ pathname, search: location.search }} replace />}
          >
            {label}
          </Tab>
        ))}
      </TabsList>
    </Tabs>
  );
}
